import { fromUnixTime } from 'date-fns/fromUnixTime'
import { isToday } from 'date-fns/isToday'
import { isYesterday } from 'date-fns/isYesterday'

import { ConnectCopy, ConnectionStatusEnums, type Member } from '../../core'
import { DATE_FORMATS_INTL, formatDate } from '../../core/constants'
import { buildDynamicCopy } from '../../core/utils/Localization'

export interface ConnectionMessageObject {
  cta?: string
  main: string
  statusName: string
  title: string
}

export const getConnectionMessages: any = (member: Member, copy: ConnectCopy) => {
  const { connection_messages } = copy

  switch (member.connection_status) {
    case ConnectionStatusEnums.CHALLENGED: {
      return {
        cta: connection_messages.challenged_cta,
        main: buildDynamicCopy(
          connection_messages.challenged_connection_description,
          member.name,
        ) as string,
        statusName: copy.connection_statuses[member.connection_status],
        title: connection_messages.challenged_connection_title,
      }
    }
    case ConnectionStatusEnums.CLOSED: {
      return {
        main: connection_messages.closed_connection_description,
        statusName: copy.connection_statuses[member.connection_status],
        title: connection_messages.closed_connection_title,
      }
    }
    case ConnectionStatusEnums.DEGRADED: {
      return {
        main: connection_messages.degraded_connection_description,
        statusName: copy.connection_statuses[member.connection_status],
        title: connection_messages.degraded_connection_title,
      }
    }
    case ConnectionStatusEnums.DELAYED: {
      return {
        main: buildDynamicCopy(
          connection_messages.delayed_connection_description,
          member.name,
        ) as string,
        statusName: copy.connection_statuses[member.connection_status],
        title: connection_messages.delayed_connection_title,
      }
    }
    case ConnectionStatusEnums.DENIED: {
      return {
        cta: connection_messages.denied_cta,
        main: buildDynamicCopy(
          connection_messages.denied_connection_description,
          member.name,
        ) as string,
        statusName: copy.connection_statuses[member.connection_status],
        title: connection_messages.denied_connection_title,
      }
    }
    case ConnectionStatusEnums.DISABLED: {
      return {
        main: connection_messages.disabled_connection_description,
        statusName: copy.connection_statuses[member.connection_status],
        title: connection_messages.disabled_connection_title,
      }
    }
    case ConnectionStatusEnums.DISCONNECTED: {
      return {
        main: buildDynamicCopy(
          connection_messages.disconnected_connection_description,
          member.name,
        ) as string,
        statusName: copy.connection_statuses[member.connection_status],
        title: connection_messages.disconnected_connection_title,
      }
    }
    case ConnectionStatusEnums.DISCONTINUED: {
      return {
        main: connection_messages.discontinued_connection_description,
        statusName: copy.connection_statuses[member.connection_status],
        title: connection_messages.discontinued_connection_title,
      }
    }
    case ConnectionStatusEnums.EXPIRED: {
      return {
        cta: connection_messages.expired_cta,
        main: connection_messages.expired_connection_description,
        statusName: copy.connection_statuses[member.connection_status],
        title: connection_messages.expired_connection_title,
      }
    }
    case ConnectionStatusEnums.FAILED: {
      return {
        main: buildDynamicCopy(
          connection_messages.failed_connection_description,
          member.name,
        ) as string,
        statusName: copy.connection_statuses[member.connection_status],
        title: connection_messages.failed_connection_title,
      }
    }
    case ConnectionStatusEnums.IMPAIRED: {
      return {
        cta: connection_messages.impaired_cta,
        main: buildDynamicCopy(
          connection_messages.impaired_connection_description,
          member.name,
        ) as string,
        statusName: copy.connection_statuses[member.connection_status],
        title: connection_messages.impaired_connection_title,
      }
    }
    case ConnectionStatusEnums.IMPEDED: {
      return {
        main: buildDynamicCopy(
          connection_messages.impeded_connection_description,
          member.name,
          member.name,
        ) as string,
        statusName: copy.connection_statuses[member.connection_status],
        title: connection_messages.impeded_connection_title,
      }
    }
    case ConnectionStatusEnums.IMPORTED: {
      return {
        cta: connection_messages.imported_cta,
        main: buildDynamicCopy(
          connection_messages.imported_connection_description,
          member.name,
        ) as string,
        statusName: copy.connection_statuses[member.connection_status],
        title: connection_messages.imported_connection_title,
      }
    }
    case ConnectionStatusEnums.LOCKED: {
      return {
        cta: connection_messages.locked_cta,
        main: buildDynamicCopy(
          connection_messages.locked_connection_description,
          member.name,
        ) as string,
        statusName: copy.connection_statuses[member.connection_status],
        title: connection_messages.locked_connection_title,
      }
    }
    case ConnectionStatusEnums.PREVENTED: {
      return {
        cta: connection_messages.prevented_cta,
        main: connection_messages.prevented_connection_description,
        statusName: copy.connection_statuses[member.connection_status],
        title: connection_messages.prevented_connection_title,
      }
    }
    case ConnectionStatusEnums.REJECTED: {
      return {
        cta: connection_messages.rejected_cta,
        main: connection_messages.rejected_connection_description,
        statusName: copy.connection_statuses[member.connection_status],
        title: connection_messages.rejected_connection_title,
      }
    }
    default: {
      const lastUpdatedCopy = () => {
        const lastUpdated = member.successfully_aggregated_at
          ? fromUnixTime(member.successfully_aggregated_at)
          : undefined
        const lastUpdatedFormattedTime =
          lastUpdated && formatDate(lastUpdated, DATE_FORMATS_INTL.TIME_STAMP_HOUR_MINUTE)

        if (!lastUpdatedFormattedTime) return undefined
        if (isToday(lastUpdated))
          return buildDynamicCopy(copy.last_updated_at, copy.today, lastUpdatedFormattedTime)
        if (isYesterday(lastUpdated))
          return buildDynamicCopy(copy.last_updated_at, copy.yesterday, lastUpdatedFormattedTime)
        return buildDynamicCopy(
          copy.last_updated_at,
          formatDate(lastUpdated, DATE_FORMATS_INTL.MONTH_DAY_YEAR),
          lastUpdatedFormattedTime,
        )
      }
      return {
        statusName: copy.connection_statuses[6], // Connected
        title: lastUpdatedCopy(),
      }
    }
  }
}
